import { Store, Package, Lock, Truck, Mail } from 'lucide-react';
import React from 'react';

/**
 * South African delivery providers.
 * This is the single source of truth for delivery options.
 */
export const DELIVERY_PROVIDERS = {
  collect: {
    id: 'collect',
    name: 'Collect from vendor',
    type: 'pickup',
    defaultRate: 0,
    estimate: 'Same day',
    description: 'Pick up from the vendor directly',
    icon: <Store size={18} />,
  },
  pep_paxi: {
    id: 'pep_paxi',
    name: 'Pep Paxi',
    type: 'store',
    defaultRate: 60,
    estimate: '3-5 business days',
    description: 'Collect from your nearest Pep store',
    icon: <Package size={18} />,
  },
  pudo: {
    id: 'pudo',
    name: 'Pudo Locker',
    type: 'locker',
    defaultRate: 70,
    estimate: '2-4 business days',
    description: 'Collect from a Pudo locker near you',
    icon: <Lock size={18} />,
  },
  courier_guy: {
    id: 'courier_guy',
    name: 'The Courier Guy',
    type: 'door',
    defaultRate: 110,
    estimate: '1-3 business days',
    description: 'Door-to-door delivery to your address',
    icon: <Truck size={18} />,
  },
  postnet: {
    id: 'postnet',
    name: 'PostNet',
    type: 'counter',
    defaultRate: 100,
    estimate: '2-3 business days',
    description: 'Collect from your nearest PostNet',
    icon: <Mail size={18} />,
  },
};

/**
 * Groups delivery types for display. Lower order = shown first.
 */
export const DELIVERY_TYPE_LABELS = {
  pickup: { label: 'Collection', order: 1 },
  locker: { label: 'Lockers', order: 2 },
  store: { label: 'Stores', order: 2 },
  counter: { label: 'Counter Pickup', order: 2 },
  door: { label: 'Door Delivery', order: 3 },
};

/**
 * Default shipping config for any pro who hasn't set their own.
 */
export const DEFAULT_SHIPPING = {
  enabledProviders: ['collect', 'pep_paxi', 'pudo', 'courier_guy', 'postnet'],
  customRates: {},
  freeShippingThreshold: 500,
  collectAddress: '',
};

/**
 * Resolve the rate for a specific provider given a pro's shipping config.
 */
export const getProviderRate = (providerId, shippingConfig) => {
  const provider = DELIVERY_PROVIDERS[providerId];
  if (!provider) return 0;
  if (providerId === 'collect') return 0;
  const custom = shippingConfig?.customRates?.[providerId];
  if (typeof custom === 'number') return custom;
  return provider.defaultRate;
};

/**
 * Get the list of providers a pro offers, sorted by delivery type order.
 */
export const getProProviders = (shippingConfig) => {
  const config = { ...DEFAULT_SHIPPING, ...(shippingConfig || {}) };
  const enabled = config.enabledProviders || DEFAULT_SHIPPING.enabledProviders;
  return enabled
    .map(id => DELIVERY_PROVIDERS[id])
    .filter(Boolean)
    .sort((a, b) => {
      const aO = DELIVERY_TYPE_LABELS[a.type]?.order ?? 99;
      const bO = DELIVERY_TYPE_LABELS[b.type]?.order ?? 99;
      return aO - bO;
    });
};

/**
 * Returns true if this provider requires a physical address (door delivery only).
 */
export const requiresAddress = (providerId) => {
  const provider = DELIVERY_PROVIDERS[providerId];
  if (!provider) return false;
  return provider.type === 'door';
};